import PropTypes from 'prop-types';
import { Suspense, lazy, useMemo } from 'react';

const DefComponent = () => null;

// Cached per componentPath so repeated renders (e.g. a persisted `data-turbo-permanent`
// mount re-rendered on every turbo:load) reuse the same lazy component reference —
// otherwise React treats each fresh `lazy()` call as a new type and force-remounts.
const lazyComponentCache = new Map();

const getLazyComponent = (componentPath) => {
  if (!lazyComponentCache.has(componentPath)) {
    lazyComponentCache.set(
      componentPath,
      // see https://webpack.js.org/api/module-methods/#magic-comments
      lazy(
        () =>
          import(
            /* webpackChunkName: "react__async__[request]" */
            /* webpackInclude: /\.js$/ */
            /* webpackMode: "lazy" */
            `renderable/${componentPath}`
          ),
      ),
    );
  }
  return lazyComponentCache.get(componentPath);
};

export const AsyncComponent = ({ componentPath, DefaultComponent = DefComponent, ...props }) => {
  const LazyComponent = useMemo(() => getLazyComponent(componentPath), [componentPath]);
  return (
    <Suspense fallback={<DefaultComponent {...props} />}>
      {/* eslint-disable-next-line react-hooks/static-components -- LazyComponent is cached
          by componentPath in getLazyComponent, so this is stable across renders */}
      <LazyComponent {...props} />
    </Suspense>
  );
};

AsyncComponent.propTypes = {
  componentPath: PropTypes.string.isRequired,
  DefaultComponent: PropTypes.elementType,
};
